import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, signal, ViewChild } from '@angular/core';
import { RegionsInfo, SitesShortForm } from '../../interfaces/regions';
import { LoadProjectionsRecord } from '../../interfaces/load-projections-record';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { LookUpService } from '../../services/lookupservice';
import { MatCardModule } from '@angular/material/card';
import { FeedstockService } from '../../services/feedstockservice';
import { NotificationService } from '../../services/notificationservice';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { Subject, takeUntil } from 'rxjs';
import { RoleService } from '../../services/role.service';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { ScreenName, LoadScheduleControlFlags } from '../../interfaces/securityPermissions';

@Component({
  selector: 'app-loadprojection',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatCardModule,
    MatSortModule,

  ],
  templateUrl: './loadprojection.component.html',
  styleUrl: './loadprojection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoadprojectionComponent {

  displayedColumns = [
    'siteName', 'scheduleDate', 'streamName', 'tankName',
    'projected_Quantity', 'haulerName', 'generatorName', 'statusName'
  ];

  // Pending overlay (id -> status)
  private updateStatusMap = signal<Map<number, number>>(new Map());

  // Dropdown state
  markets = signal<RegionsInfo[]>([]);
  selectedMarketId = signal<number>(-1);
  marketQuery = signal<string>('');

  disposalSites = signal<SitesShortForm[]>([]);
  selectedSiteId = signal<number | null>(null);
  siteQuery = signal<string>('');

  // Table records
  loadProjections = new MatTableDataSource<any>;
  private destroy$ = new Subject<void>();

  @ViewChild('marketFilterInput') marketFilterInput?: ElementRef<HTMLInputElement>;
  @ViewChild('siteFilterInput') siteFilterInput?: ElementRef<HTMLInputElement>;
  @ViewChild(MatSort) sort!: MatSort;
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  constructor(private _lookupService: LookUpService,
    private _fsService: FeedstockService, private _notificationService: NotificationService,
    private permissions: RolePermissionsService, private rolesService: RoleService) {

    this.loadMarkets();

    //When market changes: reset site, load sites, and clear pending
    effect(() => {
      const market = this.selectedMarketId();
      this.selectedSiteId.set(null);
      this.siteQuery.set('');
      this.loadDisposalSites(market);
      //this.getLoadProjections(this.selectedSiteId());
      //this.clearUpdateStatusMap();// clear overlay on filter change
    });

    effect(() => {
      const siteId = this.selectedSiteId();
      this.getLoadProjections(siteId);
      this.clearUpdateStatusMap(); // clear overlay on filter change
    });
    
    let roles = this.rolesService.getRolesAsString();
    this.permissions.getRolePermissionsNew(roles, ScreenName.LoadProjection, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    })
  }

  // Filtered views (case-insensitive)
  marketsFiltered = computed(() => {
    const q = this.marketQuery().trim().toLowerCase();
    return this.markets().filter(m => m.regionName.toLowerCase().includes(q));
  });
  sitesFiltered = computed(() => {
    const q = this.siteQuery().trim().toLowerCase();
    return this.disposalSites().filter(s => s.siteName.toLowerCase().includes(q));
  });

  // --- Market select handlers
  onMarketSelected(value: number): void {
    this.selectedMarketId.set(value);
  }
  onMarketQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.marketQuery.set(searchTerm);
  }
  onMarketOpened(open: boolean): void {
    if (open) setTimeout(() => this.marketFilterInput?.nativeElement.focus(), 0);
  }

  // --- Site select handlers
  onSiteSelected(value: number | null): void {
    this.selectedSiteId.set(value);
  }
  onSiteQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.siteQuery.set(searchTerm);
  }
  onSiteOpened(open: boolean): void {
    if (open) setTimeout(() => this.siteFilterInput?.nativeElement.focus(), 0);
  }

  private loadMarkets(): void {
    this._lookupService.getMarketsLookupForProjection().pipe(takeUntil(this.destroy$)).subscribe({
      next: (regions: RegionsInfo[]) => {
        this.markets.set(regions);
      }
    })
  }

  private loadDisposalSites(marketId: number): void {
    this._lookupService.getLPSites().pipe(takeUntil(this.destroy$)).subscribe({
      next: (sites: SitesShortForm[]) => {
        this.disposalSites.set(sites);
      }
    })
  }

  private getLoadProjections(siteId: number | null): void {
    this._fsService.getLoadProjections(siteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: LoadProjectionsRecord[]) => {
        this.loadProjections.data = result;
        this.loadProjections.sort = this.sort;
      }
    })
  }

  selectedStatusRecord(loadScheduleId: number, checked: boolean): void {
    const next = new Map(this.updateStatusMap()); // clone for immutability
    if (checked) {
      next.set(loadScheduleId, 9);
    } else {
      next.delete(loadScheduleId);
    }
    this.updateStatusMap.set(next);
  }

  isUpdateStatusDisable = computed(() => {
    return this.updateStatusMap().size == 0;
  });

  isCheckboxVisible(statusName: string): boolean {
    return statusName == 'Schedule'; 
  }

  updateStatus(): void {
    const payload = Array.from(this.updateStatusMap().keys());
    console.log(payload);
    this._fsService.updateLoadProjectionStatus(payload).subscribe({
      next: (result: boolean) => {
        this.clearUpdateStatusMap();
        this.getLoadProjections(this.selectedSiteId());
        this._notificationService.show('Status(es) updated successfully', 'success');
      }
    });
  }

  // --- trackBy helpers
  trackRow = (_: number, row: LoadProjectionsRecord) => row.id;
  trackByMarketId = (_: number, item: RegionsInfo) => item.id;
  trackBySiteId = (_: number, item: SitesShortForm) => item.id;

  private clearUpdateStatusMap(): void {
    this.updateStatusMap.set(new Map());
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
